import React, { useState, useEffect } from 'react';
import roomBookingAPI from '../services/roomBookingAPI';

const HKT = 'Asia/Hong_Kong';

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('zh-TW', {
    timeZone: HKT,
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('zh-TW', { timeZone: HKT, hour: '2-digit', minute: '2-digit', hour12: false });
};

// Get the HKT date string (YYYY-MM-DD) — UTC arithmetic, no locale dependency
const toHKTDateStr = (iso) => {
  const hktMs = new Date(iso).getTime() + 8 * 3600 * 1000;
  return new Date(hktMs).toISOString().slice(0, 10);
};

// Get HKT hour (0-23) — UTC arithmetic, no locale dependency
const toHKTHour = (iso) => (new Date(iso).getUTCHours() + 8) % 24;

// Interpret a datetime-local value as HKT and convert to UTC ISO
const hktInputToISO = (val) => new Date(val + '+08:00').toISOString();

// weekStart: get Monday of the week in HKT for a given date
const weekStart = (date) => {
  const hktDateStr = toHKTDateStr(date.toISOString());
  const d = new Date(hktDateStr + 'T00:00:00+08:00'); // midnight this day in HKT = UTC -8h
  // d.getUTCDay() is previous day's UTC weekday; add 1 to get HKT weekday (0=Sun,1=Mon…6=Sat)
  const day = (d.getUTCDay() + 1) % 7;
  const diff = day === 0 ? -6 : 1 - day; // days back to Monday
  return new Date(d.getTime() + diff * 86400000);
};

// addDays via milliseconds — safe across all timezones (HKT has no DST)
const addDays = (date, n) => new Date(date.getTime() + n * 86400000);

const DAYS_ZH = ['一', '二', '三', '四', '五', '六', '日'];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00–22:00

export default function PublicRoomBooking() {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [weekOf, setWeekOf] = useState(weekStart(new Date()));
  const [existingBookings, setExistingBookings] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [calRefreshKey, setCalRefreshKey] = useState(0);

  // Form state
  const [form, setForm] = useState({
    title: '',
    organizer_name: '',
    start_time: '',
    end_time: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Load rooms on mount
  useEffect(() => {
    roomBookingAPI.publicListRooms()
      .then((res) => {
        setRooms(res.data);
        if (res.data.length > 0) setSelectedRoom(String(res.data[0].id));
      })
      .catch(() => setError('無法載入房間列表，請稍後再試'));
  }, []);

  // Load existing bookings when room, week, or calRefreshKey changes
  useEffect(() => {
    if (!selectedRoom) return;
    const from = toHKTDateStr(weekOf.toISOString());
    const to = toHKTDateStr(addDays(weekOf, 6).toISOString());
    setLoadingCal(true);
    roomBookingAPI.publicCheckAvailability(selectedRoom, from, to)
      .then((res) => setExistingBookings(res.data))
      .catch(() => setExistingBookings([]))
      .finally(() => setLoadingCal(false));
  }, [selectedRoom, weekOf, calRefreshKey]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedRoom) { setError('請選擇房間'); return; }
    if (!form.start_time || !form.end_time) { setError('請填寫開始及結束時間'); return; }
    if (new Date(form.end_time + '+08:00') <= new Date(form.start_time + '+08:00')) {
      setError('結束時間必須晊於開始時間');
      return;
    }
    setSubmitting(true);
    try {
      await roomBookingAPI.publicCreateBooking({
        room_id: Number(selectedRoom),
        title: form.title,
        organizer_name: form.organizer_name,
        start_time: hktInputToISO(form.start_time),
        end_time: hktInputToISO(form.end_time),
        notes: form.notes || null,
      });
      setSubmitted(true);
      setCalRefreshKey((k) => k + 1); // refresh calendar so new booking shows
    } catch (err) {
      const msg = err.response?.data?.detail || '提交失敗，請稍後再試';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Calendar helpers ────────────────────────────────────────────────────
  const bookingsForDay = (dayIdx) => {
    const day = addDays(weekOf, dayIdx);
    const dayStr = toHKTDateStr(day.toISOString());
    return existingBookings.filter((b) => toHKTDateStr(b.start_time) === dayStr);
  };

  const isBooked = (dayIdx, hour) => {
    return bookingsForDay(dayIdx).some((b) => {
      const start = toHKTHour(b.start_time);
      const end = toHKTHour(b.end_time);
      return hour >= start && hour < end;
    });
  };

  const selectedRoomName = rooms.find((r) => String(r.id) === selectedRoom)?.name ?? '';

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">預訂申請已提交！</h2>
          <p className="text-gray-500 mb-6">
            您的房間預訂申請已成功提交，請等待管理員審批。<br />
            審批結果將另行通知。
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm({ title: '', organizer_name: '', start_time: '', end_time: '', notes: '' }); setError(''); }}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            再次預訂
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-600 rounded-2xl mb-4 shadow-lg">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">房間預訂</h1>
          <p className="text-gray-500 mt-1">填寫以下表格提交預訂申請</p>
        </div>

        <div className="space-y-6">
          {/* Booking Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">預訂資料</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Room selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  選擇房間 <span className="text-red-500">*</span>
                </label>
                {rooms.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">載入房間中…</p>
                ) : (
                  <select
                    required
                    value={selectedRoom}
                    onChange={(e) => setSelectedRoom(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.location ? ` — ${r.location}` : ''}
                        {r.capacity ? ` (容納 ${r.capacity} 人)` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  使用事由 <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  type="text"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="例：小組聚會、詩班排練"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Organizer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  負責人姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  type="text"
                  value={form.organizer_name}
                  onChange={(e) => set('organizer_name', e.target.value)}
                  placeholder="您的姓名"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Date/time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    開始時間 <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => set('start_time', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    結束時間 <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => set('end_time', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">備註（選填）</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="請說明使用目的、設備需求等"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || rooms.length === 0}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? '提交中…' : '提交預訂申請'}
              </button>
              <p className="text-xs text-gray-400 text-center">
                提交後需等待管理員審批，審批完成後方可使用
              </p>
            </form>
          </div>

          {/* Availability Calendar */}
          {selectedRoom && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedRoomName} — 本週使用情況
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setWeekOf(addDays(weekOf, -7))}
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-gray-500 self-center">
                    {weekOf.toLocaleDateString('zh-TW', { timeZone: HKT, month: 'short', day: 'numeric' })}
                    {' – '}
                    {addDays(weekOf, 6).toLocaleDateString('zh-TW', { timeZone: HKT, month: 'short', day: 'numeric' })}
                  </span>
                  <button
                    onClick={() => setWeekOf(addDays(weekOf, 7))}
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              {loadingCal ? (
                <p className="text-sm text-gray-400 py-6 text-center">載入中…</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="w-12 text-gray-400 font-normal pb-2">時間</th>
                        {DAYS_ZH.map((d, i) => {
                          const day = addDays(weekOf, i);
                          const isToday = toHKTDateStr(day.toISOString()) === toHKTDateStr(new Date().toISOString());
                          const hktDayNum = parseInt(toHKTDateStr(day.toISOString()).slice(8), 10);
                          return (
                            <th key={d} className={`pb-2 font-medium text-center ${isToday ? 'text-indigo-600' : 'text-gray-600'}`}>
                              <div>{`週${d}`}</div>
                              <div className={`text-xs mt-0.5 ${isToday ? 'bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto' : 'text-gray-400'}`}>
                                {hktDayNum}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {HOURS.map((hour) => (
                        <tr key={hour} className="border-t border-gray-100">
                          <td className="text-gray-400 py-1 pr-2 text-right leading-none">
                            {String(hour).padStart(2, '0')}:00
                          </td>
                          {DAYS_ZH.map((_, i) => {
                            const booked = isBooked(i, hour);
                            return (
                              <td
                                key={i}
                                title={booked ? '已預訂' : ''}
                                className={`py-1 px-0.5 ${booked ? 'bg-red-400' : 'bg-green-50'}`}
                              />
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-200" />空閒</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-400" />已預訂</span>
                  </div>
                </div>
              )}

              {/* Booking list for the week */}
              {existingBookings.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">本週預訂詳情</h3>
                  {existingBookings.map((b) => (
                    <div key={b.id} className="flex items-start gap-3 text-sm">
                      <span className="mt-0.5 w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-gray-800">{b.title}</span>
                        <span className="text-gray-500 ml-2">{fmtDate(b.start_time)} – {fmtTime(b.end_time)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
